import React from 'react';

interface StatCardProps {
  label: string;
  value: number;
  unit?: string;
  icon?: React.ReactNode;
  status?: 'good' | 'fair' | 'attention' | 'monitor';
}

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  unit = '',
  icon,
  status = 'good'
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good': return '#28A049';
      case 'fair': return '#007BC2';
      case 'attention': return '#F37D42';
      case 'monitor': return '#D20000';
      default: return '#000000';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'good':
        return (
          <svg width="18.33" height="18.33" viewBox="0 0 18.33 18.33" fill="none">
            <g transform="translate(0.83, 0.83)">
              <path d="M4.17 5.69L9.43 12.76" stroke="#28A049" strokeWidth="2" fill="none"/>
              <circle cx="9.165" cy="9.165" r="8.165" stroke="#28A049" strokeWidth="2" fill="none"/>
            </g>
          </svg>
        );
      case 'fair':
        return (
          <svg width="18.33" height="18.33" viewBox="0 0 18.33 18.33" fill="none">
            <g transform="translate(0.83, 0.83)">
              <path d="M4.17 5.69L9.43 12.76" stroke="#007BC2" strokeWidth="2" fill="none"/>
              <circle cx="9.165" cy="9.165" r="8.165" stroke="#007BC2" strokeWidth="2" fill="none"/>
            </g>
          </svg>
        );
      case 'attention':
        return (
          <svg width="18.33" height="15.83" viewBox="0 0 18.33 15.83" fill="none">
            <g transform="translate(0.83, 2.08)">
              <path d="M0 0L18.33 15.83" stroke="#F37D42" strokeWidth="2" fill="none"/>
              <path d="M9.17 5.83L9.17 12.91" stroke="#F37D42" strokeWidth="2" fill="none"/>
            </g>
          </svg>
        );
      case 'monitor':
        return (
          <svg width="14.72" height="17.34" viewBox="0 0 14.72 17.34" fill="none">
            <path d="M2.64 1.33L12.08 18.67H2.64L2.64 1.33Z" fill="#D20000"/>
          </svg>
        );
      default:
        // Default list icon for total stats
        return (
          <svg width="15" height="11.67" viewBox="0 0 15 11.67" fill="none">
            <g transform="translate(2.5, 4.17)">
              <path d="M0 0H15M0 3.83H15M0 7.67H15" stroke="#000000" strokeWidth="2"/>
            </g>
          </svg>
        );
    }
  };

  const getBoxShadow = (status: string) => {
    switch (status) {
      case 'good': return '0px 0px 5px 0px rgba(40, 160, 73, 0.2)';
      case 'fair': return '0px 0px 5px 0px rgba(14, 124, 193, 0.2)';
      case 'attention': return '0px 0px 5px 0px rgba(243, 125, 65, 0.2)';
      case 'monitor': return '0px 0px 5px 0px rgba(210, 0, 0, 0.2)';
      default: return '0px 0px 5px 0px rgba(0, 0, 0, 0.1)';
    }
  };

  return (
    <div style={{
      width: '85px',
      height: '40px',
      backgroundColor: '#FFFFFF',
      borderRadius: '5px',
      boxShadow: getBoxShadow(status),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '5px'
    }}>
      {/* Icon */}
      <div style={{
        width: '20px',
        height: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {icon || getStatusIcon(status)}
      </div>

      {/* Content */}
      <div style={{
        width: '50px',
        display: 'flex',
        flexDirection: 'column',
        gap: '-8px'
      }}>
        <div style={{
          fontFamily: 'BT Beau Sans, Inter, sans-serif',
          fontWeight: 800,
          fontSize: '12px',
          lineHeight: '2.461em', /* 29.54px / 12px = 2.461em */
          color: getStatusColor(status),
          height: '19px',
          display: 'flex',
          alignItems: 'center'
        }}>
          {value}{unit}
        </div>
        <div style={{
          fontFamily: 'BT Beau Sans, Inter, sans-serif',
          fontWeight: 300,
          fontSize: '7px',
          lineHeight: '4.22em', /* 29.54px / 7px = 4.22em */
          color: getStatusColor(status),
          width: '50px',
          height: '23px',
          display: 'flex',
          alignItems: 'flex-start'
        }}>
          {label}
        </div>
      </div>
    </div>
  );
};

export default StatCard;
